/**
 * @file 可预期错误过滤
 * @module utils/expectedErrors
 * @description 区分业务失败、表单取消、表单校验失败和真实运行时异常，避免噪音进入 Bugsink/Sentry。
 */
import type { EventHint } from '@sentry/vue'
import type { ApiResponse } from '@/types/apiResponse'

/** 业务响应错误的内部标记字段 */
const BUSINESS_RESPONSE_ERROR_MARKER = '__tdmBusinessResponseError'

/** 后端业务响应 data 的默认 JSON 值范围 */
export type BusinessResponseData =
  | string
  | number
  | boolean
  | null
  | undefined
  | BusinessResponseData[]
  | { [key: string]: BusinessResponseData }

/** 运行时对象守卫可读取的值范围 */
type RuntimeInspectableValue =
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined
  | RuntimeInspectableValue[]
  | { [key: string]: RuntimeInspectableValue }

/** Sentry 捕获前保留的原始异常类型。 */
type CapturedException = EventHint['originalException']

/** 业务响应错误负载 */
export type BusinessResponsePayload<T = BusinessResponseData> = ApiResponse<T>

/** 后端业务失败响应，用于和真实运行时异常区分 */
export class BusinessResponseError<T = BusinessResponseData> extends Error {
  /** 内部标记：该错误来自后端业务响应 */
  readonly [BUSINESS_RESPONSE_ERROR_MARKER] = true
  /** 后端业务码 */
  readonly code?: number
  /** 后端业务消息 */
  readonly msg?: string
  /** 后端业务数据 */
  readonly data?: T | null

  /** 创建后端业务失败响应错误 */
  constructor(
    payload: BusinessResponsePayload<T>,
    fallbackMessage = '业务请求失败'
  ) {
    super(payload.msg || fallbackMessage)
    this.name = 'BusinessResponseError'
    this.code = payload.code
    this.msg = payload.msg
    this.data = payload.data
  }
}

/**
 * 判断值是否为普通可检查对象。
 * @param value 外部捕获到的任意异常值。
 * @returns 值可按对象键读取时返回 true。
 */
const isRecord = (
  value: CapturedException | RuntimeInspectableValue
): value is Record<string, RuntimeInspectableValue> =>
  value !== null && typeof value === 'object'

/** Sentry 异常条目的最小字段集合。 */
interface SentryExceptionEntry {
  /** 异常类型，例如 Error 或 BusinessResponseError。 */
  type?: string
  /** 异常消息正文。 */
  value?: string
}

/** Sentry beforeSend 事件在本模块中读取的最小结构。 */
interface SentryLikeEvent {
  /** Sentry 捕获到的异常列表。 */
  exception?: {
    /** 异常值数组，可能由 SDK 或浏览器提供。 */
    values?: SentryExceptionEntry[]
  }
}

/** Sentry beforeSend hint 在本模块中读取的最小结构。 */
interface SentryLikeHint {
  /** SDK 捕获前的原始异常对象。 */
  originalException?: EventHint['originalException']
}

/**
 * 判断值是否为后端业务响应错误。
 * @param value 外部捕获到的任意异常值。
 * @returns 带有业务响应内部标记时返回 true。
 */
export const isBusinessResponseError = (
  value: CapturedException
): value is BusinessResponseError => {
  return isRecord(value) && value[BUSINESS_RESPONSE_ERROR_MARKER] === true
}

/**
 * 判断单个 Element Plus 校验错误项。
 * @param value 表单校验错误数组中的单个条目。
 * @returns 条目包含 message 或 field 字符串时返回 true。
 */
const isElementPlusValidationItem = (
  value: RuntimeInspectableValue
): boolean => {
  return (
    isRecord(value) &&
    (typeof value.message === 'string' || typeof value.field === 'string')
  )
}

/**
 * 判断值是否为 Element Plus 表单校验失败对象。
 * @param value 外部捕获到的任意异常值。
 * @returns 符合 Element Plus validate 回调错误结构时返回 true。
 */
export const isElementPlusValidationError = (
  value: CapturedException
): boolean => {
  if (!isRecord(value) || value instanceof Error) return false
  const values = Object.values(value)
  return (
    values.length > 0 &&
    values.every(
      (item) => Array.isArray(item) && item.every(isElementPlusValidationItem)
    )
  )
}

/**
 * 判断值是否为 Element Plus 确认框取消原因。
 * @param value 外部捕获到的任意异常值。
 * @returns 用户取消或关闭确认框时返回 true。
 */
export const isElementPlusCancelReason = (
  value: CapturedException
): boolean => {
  if (value === 'cancel' || value === 'close') return true
  if (value instanceof Error) {
    return value.message === 'cancel' || value.message === 'close'
  }
  return false
}

/** 允许忽略的对象上报字段 */
const EXPECTED_OBJECT_KEYS = new Set([
  'code',
  'data',
  'msg',
  'first',
  'username',
  'password',
  'repassword',
  'invitationcode',
  'email'
])

/**
 * 判断对象捕获文案是否来自可预期的业务或表单错误。
 * @param message Sentry 生成的对象捕获文案。
 * @returns 文案只包含允许忽略的业务/表单字段时返回 true。
 */
const isExpectedCapturedObjectMessage = (message: string): boolean => {
  const matched = /^Object captured as exception with keys: (.+)$/.exec(message)
  if (!matched) return false
  return matched[1]
    .split(',')
    .map((key) => key.trim())
    .every((key) => EXPECTED_OBJECT_KEYS.has(key))
}

/**
 * 从 Sentry 事件中提取异常文案。
 * @param event Sentry beforeSend 事件对象。
 * @returns 由异常类型和异常值拼接出的文案列表。
 */
const getSentryExceptionMessages = (event: SentryLikeEvent): string[] => {
  const values = event.exception?.values
  if (!Array.isArray(values)) return []
  return values
    .map((item) => {
      return [item.type, item.value].filter(Boolean).join(': ')
    })
    .filter(Boolean)
}

/**
 * 从 Sentry hint 中提取原始异常。
 * @param hint Sentry beforeSend hint 对象。
 * @returns SDK 捕获前的原始异常值。
 */
const getOriginalException = (
  hint?: SentryLikeHint
): EventHint['originalException'] | undefined => {
  return hint?.originalException
}

/**
 * 判断 Sentry 事件是否应忽略。
 * @param event Sentry beforeSend 事件对象。
 * @param hint Sentry beforeSend hint 对象，可包含原始异常。
 * @returns 可预期业务失败、表单校验失败或确认框取消时返回 true。
 */
export const shouldIgnoreSentryEvent = (
  event: SentryLikeEvent,
  hint?: SentryLikeHint
): boolean => {
  const originalException = getOriginalException(hint)
  if (isBusinessResponseError(originalException)) return true
  if (isElementPlusValidationError(originalException)) return true
  if (isElementPlusCancelReason(originalException)) return true

  return getSentryExceptionMessages(event).some((message) => {
    return (
      message === 'Error: cancel' ||
      message === 'Error: close' ||
      message.includes('BusinessResponseError') ||
      isExpectedCapturedObjectMessage(message.replace(/^[^:]+: /, ''))
    )
  })
}
